import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import taskerService, {
  type TaskerFormDataRow,
  type TaskerTask,
  type TaskerTaskConfig,
} from "../../../services/taskerService";
import { REVIEWER_TRANSITION_KEYS, type ReviewerRoleKey } from "./reviewerRoles";
import { transitionForms } from "./taskConfig";

type MetaBase = {
  roleKey: ReviewerRoleKey;
  role: string;
  decisionField: string | null;
  remarksField: string;
  tint: string;
  labelColor: string;
};

const META_BASES: readonly MetaBase[] = [
  {
    roleKey: "vm",
    role: "Vendor Manager",
    decisionField: null,
    remarksField: "vm_remarks",
    tint: "#FDECEA",
    labelColor: "#B71C1C",
  },
  {
    roleKey: "cm",
    role: "Category Manager",
    decisionField: "category_manager_decision",
    remarksField: "category_manager_remarks",
    tint: "#FFF3E0",
    labelColor: "#B7791F",
  },
  {
    roleKey: "bizfin",
    role: "Business Finance",
    decisionField: "biz_fin_decision",
    remarksField: "biz_fin_remarks",
    tint: "#E3F2FD",
    labelColor: "#0D47A1",
  },
  {
    roleKey: "compliance",
    role: "Compliance Manager",
    decisionField: "compliance_decision",
    remarksField: "compliance_remarks",
    tint: "#EDE7F6",
    labelColor: "#4527A0",
  },
] as const;

type AuthMode = "vendor" | "reviewer";

type Entry = {
  role: string;
  decision: string | null;
  remarks: string;
  createdAt: string;
  tint: string;
  labelColor: string;
};

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

type Props = {
  task: TaskerTask;
  taskConfig: TaskerTaskConfig | undefined;
  authMode: AuthMode;
  /** Hide this viewer's own decision-form entries — context should be cross-role. */
  excludeRole?: ReviewerRoleKey;
};

type ResolvedMeta = MetaBase & { formId: string };

export function PriorReviewerRemarks({
  task,
  taskConfig,
  authMode,
  excludeRole,
}: Props) {
  const activeMeta = useMemo<ResolvedMeta[]>(() => {
    const tf = transitionForms(taskConfig);
    if (!tf) return [];
    const out: ResolvedMeta[] = [];
    for (const m of META_BASES) {
      if (m.roleKey === excludeRole) continue;
      const seen = new Set<string>();
      for (const key of REVIEWER_TRANSITION_KEYS[m.roleKey]) {
        const formId = tf[key];
        if (!formId || seen.has(formId)) continue;
        seen.add(formId);
        out.push({ ...m, formId });
      }
    }
    return out;
  }, [taskConfig, excludeRole]);
  const query = useQuery({
    queryKey: [
      "prior-reviewer-remarks",
      authMode,
      task.id,
      activeMeta.map((m) => m.formId),
    ],
    queryFn: async () => {
      // Vendor JWT lacks read on reviewer decision forms — BE returns 401.
      // The interceptor's default vendor-401 handling would clear the session
      // and redirect to /login on this expected rejection, so short-circuit
      // the redirect for these calls. The try/catch below degrades to
      // empty notes so the card just doesn't render.
      const vendorOverride =
        authMode === "vendor"
          ? { skipVendor401Redirect: true, _skipVendorRefresh: true }
          : undefined;
      const rowsPerForm = await Promise.all(
        activeMeta.map(async (meta) => {
          try {
            return await taskerService.searchFormData(
              task.id,
              meta.formId,
              authMode,
              vendorOverride,
            );
          } catch {
            return [] as TaskerFormDataRow[];
          }
        }),
      );
      return rowsPerForm;
    },
    enabled: Boolean(task.id && activeMeta.length > 0),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const entries: Entry[] = useMemo(() => {
    const rowsPerForm = query.data ?? [];
    const out: Entry[] = [];
    rowsPerForm.forEach((rows, idx) => {
      const meta = activeMeta[idx];
      if (!meta) return;
      for (const row of rows) {
        const data = (row.form_data ?? {}) as Record<string, unknown>;
        const remarks = data[meta.remarksField];
        const decision = meta.decisionField
          ? (data[meta.decisionField] as string | undefined)
          : null;
        if (typeof remarks !== "string" || !remarks.trim()) continue;
        out.push({
          role: meta.role,
          decision: decision ?? null,
          remarks: remarks.trim(),
          createdAt: (row.created_at as string) ?? "",
          tint: meta.tint,
          labelColor: meta.labelColor,
        });
      }
    });
    return out.sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [query.data, activeMeta]);

  if (query.isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }
  if (entries.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: "#E0E4EF", borderRadius: "8px", mb: 2 }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "#E0E4EF",
          bgcolor: "#FAFBFF",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          Reviewer notes
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Most recent first
        </Typography>
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {entries.map((e, i) => (
          <Box
            key={`${e.role}-${e.createdAt}-${i}`}
            sx={{
              borderLeft: 3,
              borderColor: e.labelColor,
              bgcolor: e.tint,
              px: 1.5,
              py: 1,
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: e.labelColor }}>
                {e.role}
                {e.decision ? ` · ${e.decision}` : ""}
              </Typography>
              {e.createdAt && (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatTimestamp(e.createdAt)}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
              {e.remarks}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
